<template>
  <v-container
    fluid
  >
    <v-row>
      <v-col class="pb-0">
        <span class="fw600 f7">Visitors</span>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="2">
        <v-autocomplete
          v-model="locationId"
          :items="locations"
          item-text="name"
          item-value="id"
          label="Location"
          clearable
        />
      </v-col>
      <v-col cols="2">
        <v-autocomplete
          v-model="entranceId"
          :items="locationEntrances"
          item-text="name"
          item-value="id"
          label="Entrance"
          clearable
        />
      </v-col>
      <v-col
        cols="2"
        class="pb-0"
      >
        <v-autocomplete
          v-model="userId"
          label="Host"
          :items="users"
          :item-text="(user) => `${user.first_name} ${user.last_name}`"
          item-value="id"
          clearable
        />
      </v-col>
      <v-col
        cols="2"
      >
        <v-autocomplete
          v-model="statusId"
          label="Status"
          :items="statuses"
          item-text="name"
          item-value="value"
          hide-details="true"
          clearable
        >
          <template #item="{ item }">
            <v-chip
              class="mt-1 mb-1"
              small
              text-color="white"
              style="cursor: pointer;"
              :color="item ? item.color : 'white'"
            >
              {{ item ? item.name : '' }}
            </v-chip>
          </template>
          <!-- eslint-disable-next-line vue/no-template-shadow -->
          <template #selection="{ item }">
            <v-chip
              class="mt-1 mb-1"
              small
              text-color="white"
              style="cursor: pointer;"
              :color="item ? item.color : 'white'"
            >
              {{ item ? item.name : '' }}
            </v-chip>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="2">
        <v-menu
          ref="menuStartDate"
          v-model="dateMenuStartDate"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="startDate"
              readonly
              append-icon="mdi-calendar"
              v-bind="attrs"
              hide-details
              clerable
              v-on="on"
              @change="$refs.menuShipDateEnd.save(startDate)"
            >
              <template slot="label">
                Start Date
              </template>
            </v-text-field>
          </template>
          <v-date-picker
            v-model="startDate"
            no-title
            scrollable
          >
            <v-spacer />
            <v-btn
              text
              color="primary"
              @click="menu = false"
            >
              Cancel
            </v-btn>
            <v-btn
              text
              color="primary"
              @click="startDate = null"
            >
              Clear
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="2">
        <v-menu
          ref="menuEndDate"
          v-model="dateMenuEndDate"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="endDate"
              readonly
              append-icon="mdi-calendar"
              v-bind="attrs"
              hide-details
              clerable
              v-on="on"
              @change="$refs.menuShipDateEnd.save(endDate)"
            >
              <template slot="label">
                End Date
              </template>
            </v-text-field>
          </template>
          <v-date-picker
            v-model="endDate"
            no-title
            scrollable
          >
            <v-spacer />
            <v-btn
              text
              color="primary"
              @click="menu = false"
            >
              Cancel
            </v-btn>
            <v-btn
              text
              color="primary"
              @click="endDate = null"
            >
              Clear
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-spacer />
    </v-row>
    <v-row>
      <v-col class="pt-0">
        <v-card
          elevation="0"
          outlined
        >
          <v-data-table
            :headers="headers"
            :items="items"
            :footer-props="footer"
            :items-per-page="20"
            :search="search"
            :loading="loading"
            dense
            @update:options="updateOptions"
          >
            <template #[`item.id`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.id }}
              </text-highlight>
            </template>
            <template #[`item.photo_src`]="{ item }">
              <a
                @click="openItemShowImageForm(item)"
              >
                <v-icon
                  small
                  color="primary"
                >
                  fa-image
                </v-icon>
              </a>
            </template>

            <template #[`item.full_name`]="{ item }">
              <text-highlight
                :queries="search"
                style="font-weight: 600;"
              >
                {{ item.full_name }}
              </text-highlight>
            </template>
            <template #[`item.company`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.company ? item.company : '' }}
              </text-highlight>
            </template>
            <template #[`item.phone`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.phone }}
              </text-highlight>
            </template>
            <template #[`item.email`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.email }}
              </text-highlight>
            </template>
            <template #[`item.host`]="{ item }">
              <text-highlight
                :queries="search"
                style="font-weight: 600;"
              >
                {{ item.host }}
              </text-highlight>
            </template>
            <template #[`item.location_name`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.location_name }}
              </text-highlight>
            </template>
            <template #[`item.entrance_name`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.entrance_name }}
              </text-highlight>
            </template>
            <template #[`item.created_at`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.created_at }}
              </text-highlight>
            </template>
            <template #[`item.check_out_at`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item.check_out_at }}
              </text-highlight>
            </template>
            <template #[`item.status`]="{ item }">
              <v-chip
                small
                :color="item.is_checked_in ? 'green' : 'red'"
                text-color="white"
              >
                {{ item.is_checked_in ? 'CHECKED IN' : 'CHECKED OUT' }}
              </v-chip>
            </template>

            <template #[`item.actions`]="{ item }">
              <v-btn
                v-if="item.is_checked_in"
                class="ml-1 mt-1 mb-1"
                color="primary"
                outlined
                small
                @click="checkOut(item.id)"
              >
                CHECK OUT
              </v-btn>
              <!-- <v-btn
                class="ml-1 mt-1 mb-1"
                color="error"
                outlined
                small
                @click="softDelete(item.id)"
              >
                DELETE
              </v-btn> -->
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <confirm-form ref="confirmForm" />
    <v-dialog
      v-model="imageDialog"
      max-width="600px"
    >
      <v-card>
        <v-card-title>
          <span class="headline">{{ selectedItem ? selectedItem.full_name : '' }}</span>
        </v-card-title>
        <v-card-text>
          <div class="d-flex align-center pt-5">
            <v-img
              :src="photo"
              class="stock-photo"
              width="500px"
              height="500px"
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="normal"
            class="mr-2"
            @click="imageDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';

export default {
  components: {
  },
  data: () => ({
    items: [],
    inventoryClassId: null,
    headers: [
      { text: 'Id', value: 'id' },
      { text: 'Photo', value: 'photo_src' },
      { text: 'Full Name', value: 'full_name' },
      { text: 'Company', value: 'company' },
      { text: 'Phone', value: 'phone' },
      { text: 'Email', value: 'email' },
      { text: 'Host', value: 'host' },
      { text: 'Location', value: 'location_name' },
      { text: 'Entrance', value: 'entrance_name' },
      { text: 'Status', value: 'status' },
      { text: 'Created At', value: 'created_at' },
      { text: 'Checked Out At', value: 'check_out_at' },
      {
        text: 'Actions', value: 'actions', align: 'end', width: '15%',
      },
    ],
    footer: {
      'items-per-page-options': [10, 20, 50, 100],
    },
    loading: false,
    pagination: {
      itemsPerPage: 100,
      page: 1,
    },
    total: 0,
    loadingPhoto: false,
    photo: null,
    imageDialog: null,
    selectedItem: null,
    locations: [],
    locationEntrances: [],
    users: [],
    statuses: [
      { name: 'CHECKED IN', value: 1, color: 'green' },
      { name: 'CHECKED OUT', value: 2, color: 'red' },
    ],
    locationId: null,
    entranceId: null,
    userId: null,
    dateMenuStartDate: false,
    startDate: null,
    dateMenuEndDate: false,
    endDate: null,
    statusId: null,
    sort: {
      order_by_first: 'created_at',
      direction_sort: true,
    },
  }),
  computed: {
    ...mapState({
      search: (state) => state.search,
    }),
  },
  watch: {
    locationId() {
      this.get();
    },
    entranceId() {
      this.get();
    },
    userId() {
      this.get();
    },
    startDate() {
      this.get();
    },
    endDate() {
      this.get();
    },
    statusId() {
      this.get();
    },
    search() {
      this.get();
    },
    sort: {
      handler() {
        this.get();
      },
      deep: true,
    },
  },
  created() {
    this.init();
  },
  methods: {
    init() {
      this.get();
      this.getLocations();
      this.getEntrances();
      this.getUsers();
    },

    async get() {
      this.loading = true;
      const submitModel = {
        filters: {
          location_id: this.locationId,
          location_entrance_id: this.entranceId,
          host_id: this.userId,
          start_date: this.startDate,
          end_date: this.endDate,
          status_id: this.statusId,
        },
        pagination: this.pagination,
        search: this.search,
        sort: this.sort,
      };
      const res = await this.$api.visitorVisit.readData(submitModel);
      const cancelCode = 3001;
      if (res === cancelCode) {
        return;
      }
      this.items = res.data;
      this.total = res.total;
      this.loading = false;
    },

    async getLocations() {
      this.locations = await this.$api.location.get();
    },

    async getEntrances() {
      this.locationEntrances = await this.$api.locationEntrance.get();
    },

    async getUsers() {
      this.users = await this.$api.user.get();
    },

    async checkOut(id) {
      const confirm = await this.$refs.confirmForm.open({
        title: 'Are you sure that you want to check out this visitor?',
      });
      if (!confirm) return;
      if (!id) return;

      const res = await this.$api.visitorVisit.get(`checkout/${id}`);

      if (!res) {
        this.$bus.$emit('showError');
        return;
      }
      this.$bus.$emit('showSuccess');
      this.get();
    },

    async softDelete(id) {
      const confirm = await this.$refs.confirmForm.open({
        title: 'Are you sure that you want to delete this record?',
      });
      if (!confirm) return;
      if (!id) return;

      const res = await this.$api.visitorVisit.delete(id);

      if (!res) {
        this.$bus.$emit('showError');
        return;
      }
      this.$bus.$emit('showSuccess');
      this.get();
    },

    open() {
      this.$refs.lengthForm.open();
    },

    openEdit(id) {
      this.$refs.lengthForm.open(id);
    },

    success() {
      this.$bus.$emit('showSuccess');
      this.get();
    },

    async openItemShowImageForm(item) {
      const res = await this.$api.visitorVisit.get(`download/${item.photo}`, { responseType: 'blob' });
      if (!res) {
        this.$bus.$emit('showError');
        return;
      }

      const url = URL.createObjectURL(res);
      this.photo = url;
      this.imageDialog = true;
      this.selectedItem = item;
    },

    updateOptions(newOptions) {
      this.sort = {
        order_by_first: newOptions.sortBy[0],
        direction_sort: newOptions.sortDesc[0],
      };
    },
  },
};
</script>
